import { Link, useNavigate, useParams } from 'react-router-dom';
import { NotFoundPage } from '../404.tsx';
import { createSittingRecord, getSittingSchedule } from '../../lib/api-client.ts';
import {
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Spin,
  TimePicker,
  Typography,
  Upload,
} from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PlayFormItem } from '../../components/sitting/PlayFormItem.tsx';
import { useEffect } from 'react';
import dayjs from 'dayjs';
import { UploadOutlined } from '@ant-design/icons';

const { Text } = Typography;

export const SittingReportPage = () => {
  const { id } = useParams();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  if (!id) {
    return <NotFoundPage />;
  }

  const { data: sitting } = useQuery({ queryKey: ['sitting', id], queryFn: getSittingSchedule });
  const { mutate, isPending } = useMutation({ mutationFn: createSittingRecord });
  const transportationExpenseImages = Form.useWatch('transportationExpenseImages', form);

  useEffect(() => {
    if (sitting?.record.id) {
      form.setFieldsValue({
        ...sitting.record,
        startTime: dayjs(sitting.record.startTime, 'HH:mm'),
        activities: sitting.record.activities.map((activity: any) => ({
          ...activity,
          images: {
            fileList: activity.images.map((image: any) => ({
              uid: image.id,
              name: image.name,
              status: 'done',
              url: image.url,
              thumbUrl: image.url,
              response: {
                id: image.id,
                url: image.url,
              },
            })),
          },
        })),
        transportationExpenseImages: {
          fileList: sitting.record.transportationExpenseImages.map((image: any) => ({
            uid: image.id,
            name: image.name,
            status: 'done',
            url: image.url,
            thumbUrl: image.url,
            response: {
              id: image.id,
              url: image.url,
            },
          })),
        },
      });
    }
  }, [sitting]);

  if (!sitting) return <Spin />;

  const handleSubmit = (values: any) => {
    mutate(
      {
        scheduleId: id,
        children: values.children,
        startTime: values.startTime.format('HH:mm'),
        durationHours: values.durationHours,
        educationalMaterials: values.educationalMaterials,
        memo: values.memo,
        activities: values.activities.map((activity: any) => ({
          title: activity.title,
          description: activity.description,
          actionId: activity.actionId,
          actionEpisode: activity.actionEpisode,
          images: activity.images
            ? activity.images.fileList.map((file: any) => file.response.id)
            : [],
        })),
        sitterMessage: values.sitterMessage,
        transportationExpensePrice: values.transportationExpensePrice || 0,
        transportationExpenseMemo: values.transportationExpenseMemo || '',
        transportationExpenseImages: values.transportationExpenseImages
          ? values.transportationExpenseImages.fileList.map((file: any) => file.response.id)
          : [],
      },
      {
        onSuccess: () => {
          navigate(`/sittings/${id}`);
        },
      },
    );
  };

  return (
    <>
      <Breadcrumb
        style={{ margin: 16 }}
        items={[
          { title: <Link to="/">ホーム</Link> },
          { title: <Link to="/sittings">シッティング</Link> },
          { title: <Link to={`/sittings/${id}`}>詳細</Link> },
          { title: 'レポート提出' },
        ]}
      />
      <Form
        name="report-form"
        form={form}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        autoComplete={'off'}
        onFinish={handleSubmit}
        onFinishFailed={console.error}
        disabled={isPending || sitting.record.status === 'confirmed'}
      >
        <Row gutter={[16, 16]} style={{ margin: 16 }}>
          <Col span={24}>
            <Card title="基本情報">
              <Form.Item
                label="子供"
                name="children"
                rules={[{ required: true, message: '子供を選択してください' }]}
              >
                <Checkbox.Group
                  options={sitting.user.children.map((child) => ({
                    label: child.name,
                    value: child.id,
                  }))}
                />
              </Form.Item>
              <Form.Item
                label="開始時刻"
                name="startTime"
                rules={[{ required: true, message: '開始時刻を入力してください' }]}
              >
                <TimePicker format="HH:mm" />
              </Form.Item>
              <Form.Item
                label="シッティング時間"
                name="durationHours"
                rules={[{ required: true, message: 'シッティング時間を入力してください' }]}
              >
                <InputNumber step={0.5} addonAfter="時間" />
              </Form.Item>
              <Form.Item label="使用した教材" name="educationalMaterials">
                <Input.TextArea placeholder={'例：カタチ2&#13;使用しなかった場合は無記入'} />
              </Form.Item>
              <Form.Item label="備考" name="memo">
                <Input.TextArea placeholder="シッティング時間などについて" />
              </Form.Item>
            </Card>
          </Col>
          <Form.List
            name="activities"
            initialValue={[{}, {}]}
            rules={[
              {
                validator: async (_, values) => {
                  if (values.length < 2) {
                    return Promise.reject(new Error('アクティビティは2つ以上入力してください'));
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, i) => (
                  <Col key={`activity-form-${i}`} lg={12} span={24}>
                    <PlayFormItem index={field.name} key={field.key} onRemove={() => remove(i)} />
                  </Col>
                ))}
                <Col>
                  <Button type="dashed" onClick={() => add()} style={{ width: '100%' }}>
                    活動を追加
                  </Button>
                </Col>
              </>
            )}
          </Form.List>
          <Col span={24}>
            <Card title="振り返り">
              <Form.Item
                label="担当者メッセージ"
                name="sitterMessage"
                rules={[{ required: true, message: '担当者メッセージを入力してください' }]}
              >
                <Input.TextArea placeholder="シッティング全体について" />
              </Form.Item>
            </Card>
          </Col>
          <Col span={24}>
            <Card
              title="経費"
              extra={<Text type="danger">イレギュラーな場合のみ申請してください</Text>}
            >
              <Form.Item label="交通費" name="transportationExpensePrice">
                <InputNumber defaultValue={0} />
              </Form.Item>
              <Form.Item label="交通費の内容" name="transportationExpenseMemo">
                <Input />
              </Form.Item>
              <Form.Item label="画像" name="transportationExpenseImages">
                <Upload
                  name="files"
                  action={`${import.meta.env.VITE_API_URL}/blobs`}
                  listType="picture"
                  multiple
                  accept="image/*"
                  fileList={transportationExpenseImages?.fileList || []}
                >
                  <Button icon={<UploadOutlined />}>アップロード</Button>
                </Upload>
              </Form.Item>
            </Card>
          </Col>
          <Button
            type="primary"
            htmlType="submit"
            style={{ width: '100%' }}
            size="large"
            loading={isPending}
          >
            {sitting.record.id ? '再提出' : '提出'}
          </Button>
        </Row>
      </Form>
    </>
  );
};
